import { useEffect, useRef } from 'react';
import { LANGUAGE_LABELS, directionOf, type Language } from '@voxa/shared';
import { useT } from '../i18n/index.tsx';

/**
 * One pane per speaker, each showing what was said and what it became.
 *
 * Grouping by speaker rather than by language is the only arrangement that
 * reads as a conversation: two columns of "original" and "translation" put
 * both people's words in the same box, and a reader cannot tell who is
 * talking. This way each pane is one voice.
 *
 * Both panes live in this tab only. Section 9 keeps transcripts off the
 * server, so closing the page is the whole retention policy.
 */
export interface SpeakerLines {
  original: string;
  translated: string;
}

export function Subtitles({
  me,
  peer,
  myLanguage,
  peerLanguage,
}: {
  me: SpeakerLines;
  peer: SpeakerLines;
  /** What I speak and hear. */
  myLanguage: Language;
  /** What the other person speaks. */
  peerLanguage: Language;
}) {
  const t = useT();
  return (
    <div className="subtitles">
      {/* The peer first: their words are the ones being waited on. */}
      <Pane
        title={t.subtitles.them}
        lines={peer}
        originalLanguage={peerLanguage}
        translatedLanguage={myLanguage}
        placeholder={t.subtitles.themEmpty}
      />
      <Pane
        title={t.subtitles.you}
        lines={me}
        originalLanguage={myLanguage}
        translatedLanguage={peerLanguage}
        // My half comes from the other browser over the peer connection, so
        // it stays empty until that link is up — say so rather than look broken.
        placeholder={t.subtitles.youEmpty}
      />
    </div>
  );
}

function Pane({
  title,
  lines,
  originalLanguage,
  translatedLanguage,
  placeholder,
}: {
  title: string;
  lines: SpeakerLines;
  originalLanguage: Language;
  translatedLanguage: Language;
  placeholder: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Follow the text as it streams, so the newest words are the visible ones.
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.original, lines.translated]);

  const empty = lines.original === '' && lines.translated === '';

  return (
    <div className="subtitle-pane" ref={ref}>
      <h3>{title}</h3>
      {empty ? (
        <span className="subtitle-empty">{placeholder}</span>
      ) : (
        <>
          {/*
            `dir` per line, not per page: the two lines in a pane are different
            languages, and one of them may run the other way. A Hebrew speaker's
            words are right-to-left whichever language the interface is in, and
            without this they would be laid out as if they were not.
          */}
          <p
            className="subtitle-line original"
            lang={originalLanguage}
            dir={directionOf(originalLanguage)}
          >
            <span className="subtitle-lang">{LANGUAGE_LABELS[originalLanguage]}</span>
            {lines.original || '…'}
          </p>
          <p
            className="subtitle-line translated"
            lang={translatedLanguage}
            dir={directionOf(translatedLanguage)}
          >
            <span className="subtitle-lang">{LANGUAGE_LABELS[translatedLanguage]}</span>
            {lines.translated || '…'}
          </p>
        </>
      )}
    </div>
  );
}
